import axiosInstance from "@/api/axiosInstance";

// --- Interfaces ---

export interface PaginatedResponse<T> {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  items: T[];
}

export interface SpotWindEntry {
  spotId: number;
  spotName: string;
  latitude: number;
  longitude: number;
  countryName: string;
  alpha2: string;
  totalSessions: number;
  providerCount: number;
  liveCount: number;
  forecastCount: number;
  hasEnabled: boolean;
  configSource: string;
}

export interface CountryWindEntry {
  countryId: number;
  countryName: string;
  alpha2: string;
  spotCount: number;
  providerCount: number;
  models: string;
  configured: boolean;
}

export interface WindProviderConfig {
  id?: number;
  spotId?: number;
  spotName?: string;
  countryId?: number;
  countryName?: string;
  alpha2?: string;
  provider: string;
  priority: number;
  enabled: boolean;
  dataType: string;
  stationId?: string;
  modelId?: string;
  apiKeyRef?: string;
  endpointUrl?: string;
  config?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WindModelOption {
  modelId: string;
  modelName: string;
  resolution: string;
  coverage: string;
  available: boolean;
}

export interface WindStats {
  spotsConfigured: number;
  totalSpots: number;
  countriesConfigured: number;
  totalCountries: number;
  openmeteoConfigs: number;
  windguruConfigs: number;
  liveConfigs: number;
  forecastConfigs: number;
  totalSpotConfigs: number;
  totalCountryConfigs: number;
}

export interface WindModelPreview {
  modelId: string;
  modelName: string;
  hourly: Array<{
    time: string;
    windSpeed: number;
    windDirection: number;
    windGusts: number;
    temperature: number;
  }>;
}

// --- Service ---

export const WindProviderService = {
  // Paginated browsing
  async getSpotsPaginated(
    page: number,
    limit: number,
    search?: string,
    filter?: string,
    sort?: string,
    sortDir?: string
  ): Promise<PaginatedResponse<SpotWindEntry>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (filter && filter !== "all") params.set("filter", filter);
    if (sort) params.set("sort", sort);
    if (sortDir) params.set("sortDir", sortDir);
    const response = await axiosInstance.get(`/api/admin/wind/spots?${params}`);
    return response.data;
  },

  async getCountriesPaginated(
    page: number,
    limit: number,
    search?: string,
    filter?: string,
    sort?: string,
    sortDir?: string
  ): Promise<PaginatedResponse<CountryWindEntry>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (filter && filter !== "all") params.set("filter", filter);
    if (sort) params.set("sort", sort);
    if (sortDir) params.set("sortDir", sortDir);
    const response = await axiosInstance.get(`/api/admin/wind/countries?${params}`);
    return response.data;
  },

  // Stats
  async getStats(): Promise<WindStats> {
    const response = await axiosInstance.get("/api/admin/wind/stats");
    return response.data;
  },

  // Spot config CRUD (uses existing WindAdminService endpoints)
  async getSpotConfigs(spotId: number): Promise<WindProviderConfig[]> {
    const response = await axiosInstance.get(`/api/admin/wind/config/spot/${spotId}`);
    return response.data;
  },

  async createSpotConfig(config: Partial<WindProviderConfig>): Promise<{ id: number }> {
    const response = await axiosInstance.post("/api/admin/wind/config", config);
    return response.data;
  },

  async updateSpotConfig(id: number, config: Partial<WindProviderConfig>): Promise<void> {
    await axiosInstance.put(`/api/admin/wind/config/${id}`, config);
  },

  async deleteSpotConfig(id: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/wind/config/${id}`);
  },

  // Country config CRUD
  async getCountryConfigs(countryId: number): Promise<WindProviderConfig[]> {
    const response = await axiosInstance.get(`/api/admin/wind/config/country/${countryId}`);
    return response.data;
  },

  async getAllCountryConfigs(): Promise<WindProviderConfig[]> {
    const response = await axiosInstance.get("/api/admin/wind/config/country");
    return response.data;
  },

  async createCountryConfig(config: Partial<WindProviderConfig>): Promise<{ id: number }> {
    const response = await axiosInstance.post("/api/admin/wind/config/country", config);
    return response.data;
  },

  async updateCountryConfig(id: number, config: Partial<WindProviderConfig>): Promise<void> {
    await axiosInstance.put(`/api/admin/wind/config/country/${id}`, config);
  },

  async deleteCountryConfig(id: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/wind/config/country/${id}`);
  },

  // Resolved config for a spot (spot > country > default)
  async getResolvedConfig(spotId: number): Promise<any> {
    const response = await axiosInstance.get(`/api/admin/wind/config/spot/${spotId}/resolved`);
    return response.data;
  },

  // Models & Preview
  async getAvailableModels(lat?: number, lon?: number): Promise<WindModelOption[]> {
    const params = lat != null && lon != null ? `?lat=${lat}&lon=${lon}` : "";
    const response = await axiosInstance.get(`/api/admin/wind/models${params}`);
    return response.data;
  },

  async getWindPreview(lat: number, lon: number): Promise<WindModelPreview[]> {
    const response = await axiosInstance.get(`/api/admin/wind/preview?lat=${lat}&lon=${lon}`);
    return response.data;
  },

  // Stats dashboard
  async getTopSpots(limit: number = 20): Promise<any[]> {
    const response = await axiosInstance.get(`/api/admin/wind/stats/top-spots?limit=${limit}`);
    return response.data;
  },

  async getRecentActivity(limit: number = 20): Promise<any[]> {
    const response = await axiosInstance.get(`/api/admin/wind/stats/recent-enrichments?limit=${limit}`);
    return response.data;
  },

  async getProviderDistribution(): Promise<any[]> {
    const response = await axiosInstance.get("/api/admin/wind/stats/providers");
    return response.data;
  },

  // Manual enrichment triggers
  async enrichSession(sessionId: number, clear: boolean = false): Promise<any> {
    const response = await axiosInstance.post(`/api/admin/wind/enrich-session?sessionId=${sessionId}&clear=${clear}`);
    return response.data;
  },

  async enrichSpot(spotId: number, clear: boolean = false): Promise<any> {
    const response = await axiosInstance.post(`/api/admin/wind/enrich-spot?spotId=${spotId}&clear=${clear}`);
    return response.data;
  },
};

export default WindProviderService;
